"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Check, FileText, Upload, Calendar, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const QuoteForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="56114" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <Check className="size-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Quote Request Received</h3>
        <p className="text-muted-foreground max-w-md font-sans">
          {successMessage || "Thank you for your inquiry. Our procurement team will review your bulk requirements and provide a detailed commercial proposal within 24 hours."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="company_name" rules={{
        required: "Company Name/ABN is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Company Name / ABN <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="e.g., BuildCorp Australia Pty Ltd" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contact_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Contact Person <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="email" rules={{
        required: "Business Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Business Email <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="procurement@company.com.au" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Phone Number <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 400 000 000" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="product_category" rules={{
        required: "Please select a material category"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Material Category <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full items-center justify-between rounded-sm border border-input bg-transparent px-3 py-1 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-sans" {...field}>
                    <option value="" disabled>Select material type</option>
                    <option value="Cement & Concrete">Cement & Concrete</option>
                    <option value="Steel & Metal">Steel & Metal</option>
                    <option value="Plumbing & Piping">Plumbing & Piping</option>
                    <option value="Electrical Hardware">Electrical Hardware</option>
                    <option value="Tools & Equipment">Tools & Equipment</option>
                    <option value="General Building Supplies">General Building Supplies</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="quantity" rules={{
        min: {
          value: 1,
          message: "Quantity must be at least 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Estimated Quantity</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 500 units" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono text-secondary">
                For bulk wholesale pricing calculations.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="details" rules={{
      required: "Project details are required",
      minLength: {
        value: 20,
        message: "Please provide at least 20 characters of detail"
      },
      maxLength: {
        value: 2000,
        message: "Details cannot exceed 2000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans font-semibold text-foreground">Project Specifics & Site Requirements <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Provide material grades, site delivery access, dimensions, and specific Australian standard compliance needs." className="min-h-[120px] resize-y border-input focus:ring-amber-500 rounded-sm font-sans" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="attachment" render={({
        field: {
          value,
          onChange,
          ...fieldProps
        }
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Upload Project Specs / Plans (optional)</FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <Input type="file" className="cursor-pointer text-muted-foreground file:mr-4 file:py-1 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-bold file:bg-amber-500 file:text-slate-950 hover:file:bg-amber-600" onChange={e => {
              onChange(e.target.files?.[0]?.name || "");
            }} {...fieldProps} />
                </div>
              </FormControl>
              <FormDescription className="text-xs font-mono text-secondary">
                Attach technical drawings or material lists.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="deadline" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold text-foreground">Required Delivery Date</FormLabel>
              <FormControl>
                <Input type="date" className="border-input focus:ring-amber-500 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono text-secondary">
                Target date for initial site delivery.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 rounded-sm bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2 font-sans">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold border-none shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Generating Commercial Quote...
          </> : <>
            <Send className="mr-2 size-4" />
            Submit Wholesale Quote Request
          </>}
      </Button>
    </Form>;
};
export default function GetAPrecisionQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="request-a-quote" className="relative py-20 bg-background text-foreground overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] rounded-full bg-amber-500/5 blur-3xl opacity-50" />
        <div className="absolute top-[40%] -left-[10%] w-[400px] h-[400px] rounded-full bg-primary/5 blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid lg:grid-cols-12 gap-12 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-sm border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-xs font-mono font-bold uppercase tracking-wider text-amber-600 dark:text-amber-500">
                <Clock className="mr-2 size-3.5" />
                24-Hour Trade Proposal
              </div>
              <h2 className="text-3xl md:text-5xl font-serif font-extrabold tracking-tighter text-primary">
                Request a Bulk Wholesale Quote
              </h2>
              <p className="text-lg text-secondary leading-relaxed font-sans">
                Partner with Brendon J Building Material Wholesalers for reliable supply chains across Australia. 
                Fill out our technical RFQ form to receive a comprehensive trade proposal tailored to your project scale.
              </p>
              <div className="text-xs font-mono text-secondary pt-2">
                ACN: 669 488 386 | EST. 2023
              </div>
            </div>

            <div className="space-y-6 pt-4">
              

              <div className="flex items-start gap-4 p-4 rounded-sm bg-card border border-border shadow-sm hover:border-amber-500/50 transition-colors">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-primary text-primary-foreground">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-foreground">Email RFQs</h3>
                  <p className="text-sm text-secondary mt-1 font-sans">Send site plans and material lists directly.</p>
                  <Link to="mailto:info@brendonjbuilding.com.au" className="block mt-2 font-mono text-sm font-bold text-amber-600 hover:underline">
                    info@brendonjbuilding.com.au
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-sm bg-card border border-border shadow-sm hover:border-amber-500/50 transition-colors">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-primary text-primary-foreground">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-foreground">Swan Hill Facility</h3>
                  <p className="text-sm text-secondary mt-1 font-sans">
                    31-33 Beveridge St, Swan Hill VIC 3585
                  </p>
                  <div className="mt-2 text-xs font-mono text-secondary uppercase tracking-tighter">
                    Mon - Sat: 08:00 - 18:00
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-t-4 border-t-amber-500 shadow-2xl bg-card rounded-sm">
              <CardHeader className="pb-6 border-b border-border/50">
                <CardTitle className="text-2xl font-serif font-bold flex items-center gap-2">
                  <FileText className="size-6 text-amber-500" />
                  Commercial Quote Request
                </CardTitle>
                <CardDescription className="font-sans">
                  Please provide detailed specifications to ensure accurate trade pricing.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formKey="request_quote_form_contact" formId="request_quote_form_contact" sectionName="request-a-quote" defaultValues={{
                company_name: "",
                contact_name: "",
                email: "",
                phone: "",
                product_category: "",
                quantity: "",
                details: "",
                attachment: "",
                deadline: ""
              }} validationRules={{
                company_name: {
                  required: true
                },
                contact_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                product_category: {
                  required: true
                },
                quantity: {
                  min: 1
                },
                details: {
                  required: true,
                  minLength: 20,
                  maxLength: 2000
                }
              }} submitText="Submit Wholesale Quote Request" successMessage="Thank you! Your wholesale quote request has been received. Our team will contact you shortly with a detailed proposal.">
                  <QuoteForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}